import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@radix-ui/react-toggle-group';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMeasure } from '@/hooks/useMeasure';

import { popover } from '@/styles/popover';
import { tabsStyles } from '@/styles/tabs';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import type { ChainFilterOption } from './types';

const GAP = 8;
const MAX_VISIBLE_CHAINS = 6;
const MORE_ACCESSORY_WIDTH = 16 + 6;
const MORE_KEY = 'more';
const { container, tab } = tabsStyles({ variant: 'secondary' });

type Props = {
  chains: ChainFilterOption[];
  selected: number | 'all';
  onSelect: (id: number | 'all') => void;
};

type FormattedChain = ChainFilterOption & { balance: string };

const itemKey = (id: ChainFilterOption['id']) => String(id);

export function PortfolioChainFilters({ chains, selected, onSelect }: Props) {
  const { formatFiat } = useFiatDisplay();
  const { base, item } = popover();
  const containerRef = useRef<HTMLDivElement>(null);
  const itemWidthsRef = useRef(new Map<string, number>());
  const [measureVersion, setMeasureVersion] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {
    nodeRef,
    bounds: { width: containerWidth },
  } = useMeasure<HTMLDivElement>();
  const mergedRef = useMemo(() => mergeRefs([containerRef, nodeRef]), [nodeRef]);
  const formattedChains = useMemo(
    () => chains.map((chain) => ({ ...chain, balance: formatFiat(chain.balanceUsd) })),
    [chains, formatFiat],
  );
  const allChain = formattedChains.find(({ id }) => id === 'all');
  const realChains = useMemo(
    () =>
      formattedChains
        .filter(({ id }) => id !== 'all')
        .sort((chainA, chainB) => chainB.balanceUsd - chainA.balanceUsd),
    [formattedChains],
  );
  const isSingleChain = realChains.length === 1;
  const effectiveSelected = isSingleChain ? realChains[0].id : selected;
  const pillCandidates = useMemo(
    () =>
      isSingleChain
        ? realChains
        : [...(allChain ? [allChain] : []), ...realChains.slice(0, MAX_VISIBLE_CHAINS)],
    [allChain, isSingleChain, realChains],
  );
  const baseDropdownItems = useMemo(
    () => (isSingleChain ? [] : realChains.slice(MAX_VISIBLE_CHAINS)),
    [isSingleChain, realChains],
  );
  const measurementKey = formattedChains
    .map(({ id, balance }) => `${itemKey(id)}:${balance}`)
    .join('|');

  useLayoutEffect(() => {
    setMeasureVersion((version) => version + 1);
  }, [measurementKey]);

  const visibleCount = useMemo(() => {
    if (pillCandidates.length <= 1) return pillCandidates.length;
    if (measureVersion === 0 || containerWidth <= 0) return 0;

    const widths = itemWidthsRef.current;
    if (formattedChains.some(({ id }) => !widths.has(itemKey(id)))) return 0;

    const totalPillWidth = pillCandidates.reduce(
      (sum, chain, index) => sum + widths.get(itemKey(chain.id))! + (index > 0 ? GAP : 0),
      0,
    );
    if (baseDropdownItems.length === 0 && totalPillWidth <= containerWidth) {
      return pillCandidates.length;
    }

    const moreWidth = Math.max(
      widths.get(MORE_KEY) ?? 0,
      ...realChains.map(({ id }) => widths.get(itemKey(id))! + MORE_ACCESSORY_WIDTH),
    );
    const availableWidth = containerWidth - moreWidth - GAP;
    let usedWidth = 0;
    let count = 0;

    for (const chain of pillCandidates) {
      const nextWidth = widths.get(itemKey(chain.id))! + (count > 0 ? GAP : 0);
      if (usedWidth + nextWidth > availableWidth) break;
      usedWidth += nextWidth;
      count += 1;
    }

    return count;
  }, [
    baseDropdownItems.length,
    containerWidth,
    formattedChains,
    measureVersion,
    pillCandidates,
    realChains,
  ]);

  const toggleItems = pillCandidates.slice(0, visibleCount);
  const dropdownItems = [...pillCandidates.slice(visibleCount), ...baseDropdownItems];
  const selectedDropdownItem = dropdownItems.find(({ id }) => id === effectiveSelected);

  const handleSelect = (value: string) => {
    if (!value) return;
    onSelect(value === 'all' ? 'all' : Number(value));
  };

  const pillContent = (chain: FormattedChain) => (
    <>
      {chain.id === 'all' ? (
        <span>All</span>
      ) : (
        <ChainAvatar chainId={chain.id} alt={chain.name} size={16} />
      )}
      <span>{chain.balance}</span>
    </>
  );

  const menuItems = (items: FormattedChain[]) =>
    items.map((chain) => (
      <DropdownMenuItem className={item()} key={chain.id} onSelect={() => onSelect(chain.id)}>
        {chain.id !== 'all' && (
          <ChainAvatar chainId={chain.id} alt={chain.name} size={16} className="mr-2" />
        )}
        <span>{chain.name}</span>
        <span className="ml-auto pl-4">{chain.balance}</span>
        <span className="ml-2 flex w-4">{effectiveSelected === chain.id && <CheckmarkIcon />}</span>
      </DropdownMenuItem>
    ));

  const chainPill = (chain: FormattedChain) => {
    const control = (
      <ToggleGroupItem
        className={tab({
          active: effectiveSelected === chain.id,
          class: ['whitespace-nowrap', 'shrink-0', 'gap-1.5', 'px-2.5'],
        })}
        key={chain.id}
        value={String(chain.id)}
      >
        {pillContent(chain)}
      </ToggleGroupItem>
    );

    if (chain.id === 'all') return control;

    return (
      <MatchaTooltip key={chain.id}>
        <MatchaTooltipTrigger>
          <div className="shrink-0">{control}</div>
        </MatchaTooltipTrigger>
        <MatchaTooltipContent>{chain.name}</MatchaTooltipContent>
        <MatchaTooltipPopover />
      </MatchaTooltip>
    );
  };

  return (
    <div ref={mergedRef} className="relative w-full min-w-0">
      <div className="flex justify-end gap-x-2 overflow-hidden desktop:justify-start">
        <ToggleGroup
          aria-label="Filter portfolio by chain"
          className={container({ class: ['shrink-0', 'overflow-x-visible'] })}
          onValueChange={handleSelect}
          type="single"
          value={String(effectiveSelected)}
        >
          {toggleItems.map(chainPill)}
        </ToggleGroup>

        {dropdownItems.length > 0 && (
          <DropdownMenu
            /* Keep modal false so dismissing the sticky parent cannot leave a blocking overlay. */
            modal={false}
            onOpenChange={setIsDropdownOpen}
            open={isDropdownOpen}
          >
            <DropdownMenuTrigger
              className={tab({
                active: selectedDropdownItem != null,
                class: ['whitespace-nowrap', 'shrink-0', 'gap-1.5', 'px-2.5'],
              })}
            >
              {selectedDropdownItem ? (
                selectedDropdownItem.id === 'all' ? (
                  <span>All chains</span>
                ) : (
                  pillContent(selectedDropdownItem)
                )
              ) : (
                <span>More</span>
              )}
              <ChevronDownIcon />
            </DropdownMenuTrigger>
            <DropdownMenuPortal container={containerRef.current}>
              <DropdownMenuContent
                align="end"
                className={base({
                  class: 'max-h-[calc(var(--radix-dropdown-menu-content-available-height)-8px)]',
                })}
              >
                {menuItems(dropdownItems)}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        )}
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none absolute invisible flex gap-x-2 whitespace-nowrap"
      >
        {formattedChains.map((chain) => (
          <div
            className={tab({
              class: ['whitespace-nowrap', 'shrink-0', 'gap-1.5', 'px-2.5'],
            })}
            key={chain.id}
            ref={(node) => {
              if (node) {
                itemWidthsRef.current.set(itemKey(chain.id), node.getBoundingClientRect().width);
              }
            }}
          >
            {pillContent(chain)}
          </div>
        ))}
        <div
          className={tab({ class: ['whitespace-nowrap', 'shrink-0', 'gap-1.5', 'px-2.5'] })}
          ref={(node) => {
            if (node) itemWidthsRef.current.set(MORE_KEY, node.getBoundingClientRect().width);
          }}
        >
          <span>More</span>
          <ChevronDownIcon />
        </div>
      </div>
    </div>
  );
}
